/**
 * POS Staff Management Page
 * =========================
 * 
 * Manage POS staff with role-based permissions.
 * Roles: Admin (full), Manager (reports/discounts/refunds), Cashier (checkout only)
 */

import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  ArrowLeft,
  KeyRound,
  Mail,
  User,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const ROLE_ICONS = {
  admin: ShieldCheck,
  manager: Shield,
  cashier: ShieldAlert,
};

const ROLE_COLORS = {
  admin: "text-indigo-400 bg-indigo-400/10",
  manager: "text-emerald-400 bg-emerald-400/10",
  cashier: "text-amber-400 bg-amber-400/10",
};

const POSStaffPage = () => {
  const { token } = useAuth();
  
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    pin: "",
    pos_role: "cashier",
    is_active: true,
  });
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [staffRes, rolesRes] = await Promise.all([
        fetch(`${API}/pos/staff`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/pos/staff/roles`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      if (staffRes.ok) {
        const data = await staffRes.json();
        setStaff(data);
      }
      
      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setRoles(data.roles || []);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load staff data");
    } finally {
      setLoading(false);
    }
  };
  
  const openAddModal = () => {
    setEditingStaff(null);
    setFormData({
      name: "",
      email: "",
      pin: "",
      pos_role: "cashier",
      is_active: true,
    });
    setShowModal(true);
  };
  
  const openEditModal = (s) => {
    setEditingStaff(s);
    setFormData({
      name: s.name,
      email: s.email,
      pin: "",
      pos_role: s.pos_role,
      is_active: s.is_active,
    });
    setShowModal(true);
  };
  
  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }
    
    if (!editingStaff && (!formData.pin || formData.pin.length < 4)) {
      toast.error("PIN must be at least 4 digits");
      return;
    }
    
    setSaving(true);
    
    try {
      let url = `${API}/pos/staff`;
      let method = "POST";
      let body = { ...formData };
      
      if (editingStaff) {
        url = `${API}/pos/staff/${editingStaff.staff_id}`;
        method = "PUT";
        // Only include PIN if changed
        if (!body.pin) delete body.pin;
        delete body.email; // Can't change email
      }
      
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to save staff");
      }
      
      toast.success(editingStaff ? "Staff updated" : "Staff created");
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async (staffId) => {
    if (!confirm("Are you sure you want to delete this staff member?")) return;
    
    try {
      const res = await fetch(`${API}/pos/staff/${staffId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) throw new Error("Failed to delete");
      
      toast.success("Staff deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete staff");
    }
  };
  
  const handleToggleActive = async (s) => {
    try {
      const res = await fetch(`${API}/pos/staff/${s.staff_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: !s.is_active }),
      });
      
      if (!res.ok) throw new Error("Failed to update");
      
      toast.success(s.is_active ? "Staff deactivated" : "Staff activated");
      fetchData();
    } catch (err) {
      toast.error("Failed to update staff");
    }
  };
  
  const getRoleInfo = (role) => roles.find(r => r.role === role);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/merchant/pos" className="text-gray-500 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Users className="h-6 w-6 text-indigo-400" />
                POS Staff Management
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                Manage staff members and their permissions
              </p>
            </div>
          </div>
          <Button onClick={openAddModal} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-staff-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Staff
          </Button>
        </div>
        
        {/* Role Legend */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {roles.map(role => {
            const Icon = ROLE_ICONS[role.role] || Shield;
            return (
              <div
                key={role.role}
                className={`rounded-xl border border-gray-200 p-4 ${ROLE_COLORS[role.role]}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-5 w-5" />
                  <span className="font-semibold capitalize">{role.name}</span>
                </div>
                <p className="text-sm opacity-80">{role.description}</p>
                <p className="text-xs mt-2 opacity-60">
                  Max discount: {role.max_discount_percent}%
                </p>
              </div>
            );
          })}
        </div>
        
        {/* Staff List */}
        {staff.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
            <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 font-medium">No staff members yet</p>
            <p className="text-gray-400 text-sm mt-1">Add your first team member to get started</p>
            <Button onClick={openAddModal} className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Add First Staff Member
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {staff.map(s => {
              const Icon = ROLE_ICONS[s.pos_role] || Shield;
              const roleInfo = getRoleInfo(s.pos_role);
              
              return (
                <div
                  key={s.staff_id}
                  className={`bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-4 ${!s.is_active && "opacity-50"}`}
                  data-testid={`staff-${s.staff_id}`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${ROLE_COLORS[s.pos_role]}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{s.name}</span>
                      {!s.is_active && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Inactive</span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm">{s.email}</p>
                  </div>
                  
                  <div className="text-right">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm capitalize ${ROLE_COLORS[s.pos_role]}`}>
                      {roleInfo?.name || s.pos_role}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Max {s.max_discount_percent}% discount
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(s)}
                      className="text-gray-500 hover:text-gray-900"
                      title={s.is_active ? "Deactivate" : "Activate"}
                    >
                      {s.is_active ? (
                        <ToggleRight className="h-5 w-5 text-emerald-400" />
                      ) : (
                        <ToggleLeft className="h-5 w-5" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditModal(s)}
                      className="text-gray-500 hover:text-indigo-400"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(s.staff_id)}
                      className="text-gray-500 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingStaff ? "Edit Staff Member" : "Add Staff Member"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-gray-500 flex items-center gap-1">
                <User className="h-3 w-3" /> Name
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
                className="bg-gray-100 border-gray-200 mt-1"
                data-testid="staff-name-input"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-500 flex items-center gap-1">
                <Mail className="h-3 w-3" /> Email
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                disabled={!!editingStaff}
                className="bg-gray-100 border-gray-200 mt-1"
                data-testid="staff-email-input"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-500 flex items-center gap-1">
                <KeyRound className="h-3 w-3" /> PIN (4-6 digits)
              </label>
              <Input
                type="password"
                value={formData.pin}
                onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                placeholder={editingStaff ? "Leave blank to keep current" : "1234"}
                className="bg-gray-100 border-gray-200 mt-1"
                data-testid="staff-pin-input"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-500 flex items-center gap-1">
                <Shield className="h-3 w-3" /> Role
              </label>
              <Select
                value={formData.pos_role}
                onValueChange={(v) => setFormData({ ...formData, pos_role: v })}
              >
                <SelectTrigger className="bg-gray-100 border-gray-200 mt-1" data-testid="staff-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-100 border-gray-200">
                  {roles.map(r => (
                    <SelectItem key={r.role} value={r.role} className="capitalize">
                      {r.name} - {r.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Role permissions preview */}
            {formData.pos_role && (
              <div className={`p-3 rounded-lg ${ROLE_COLORS[formData.pos_role]}`}>
                <p className="text-sm font-medium capitalize">
                  {getRoleInfo(formData.pos_role)?.name} Permissions
                </p>
                <p className="text-xs mt-1 opacity-80">
                  {getRoleInfo(formData.pos_role)?.description}
                </p>
                <p className="text-xs mt-1 opacity-60">
                  Max discount: {getRoleInfo(formData.pos_role)?.max_discount_percent}%
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-gray-200">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-staff-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingStaff ? "Save Changes" : "Add Staff"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSStaffPage;
